// weekPlan.js — the Sunday "Plan my week" pre-fill (2026-10-20). Deterministic,
// no AI: each weekly habit / workout target is spread across the plan week on
// the weekdays the user ACTUALLY did it in the 7 days before the plan starts,
// filled out evenly when they did it less often than the target. The user then
// adjusts days and times, and Save turns each planned day into a
// coach_commitments row (source 'plan') — Up next, the hour-ahead reminder and
// the "Did it happen?" resolution all already run on that table.
//
// Pure ESM, unit tested. Dates are local 'YYYY-MM-DD' strings.

const MS = 86400000;
const parse = (d) => { const [y, m, dd] = String(d).slice(0, 10).split('-').map(Number); return Date.UTC(y, m - 1, dd); };
const fmt = (ms) => new Date(ms).toISOString().slice(0, 10);
export const addDays = (d, n) => fmt(parse(d) + n * MS);
// 0 = Monday … 6 = Sunday
export const weekdayIdx = (d) => (new Date(parse(d)).getUTCDay() + 6) % 7;
export const WEEKDAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * The days to plan: Saturday or Sunday plans NEXT Monday..Sunday; any other
 * day plans the rest of THIS week (today..Sunday).
 */
export function planWeekDays(today) {
  const wd = weekdayIdx(today);
  const start = wd >= 5 ? addDays(today, 7 - wd) : today;
  const end = addDays(start, 6 - weekdayIdx(start));
  const out = [];
  for (let d = start; d <= end; d = addDays(d, 1)) out.push(d);
  return out;
}

// Choose `n` of `allowed` weekday indices: the user's own weekdays first
// (`preferred`, thinned evenly if more than n), then the allowed days furthest
// from what is already chosen (ties → earliest), so a 3x target with one
// Monday habit becomes Mon / Thu / Sat, not Mon / Tue / Wed.
export function chooseDays(n, allowed, preferred = []) {
  const allow = [...new Set(allowed)].sort((a, b) => a - b);
  if (n <= 0 || !allow.length) return [];
  if (n >= allow.length) return allow;
  let picked = allow.filter(d => preferred.includes(d));
  if (picked.length > n) {
    const step = picked.length / n;
    picked = Array.from({ length: n }, (_, i) => picked[Math.floor(i * step)]);
  }
  while (picked.length < n) {
    let best = null, bestGap = -1;
    for (const d of allow) {
      if (picked.includes(d)) continue;
      const gap = picked.length ? Math.min(...picked.map(p => Math.abs(p - d))) : 7 - d; // empty → earliest
      if (gap > bestGap) { best = d; bestGap = gap; }
    }
    picked.push(best);
  }
  return picked.sort((a, b) => a - b);
}

const parseHist = (h) => {
  let v = h;
  if (typeof v === 'string') { try { v = JSON.parse(v); } catch { v = []; } }
  return Array.isArray(v) ? v.map(d => String(d).slice(0, 10)) : [];
};

// Typical start "HH:00" from workouts' start_at in the reference window, in
// `zone` (median hour), else null — habits carry no clock.
function typicalHour(workouts, from, to, zone) {
  const hours = (workouts || [])
    .filter(w => w && w.start_at && w.date >= from && w.date <= to)
    .map(w => Number(new Intl.DateTimeFormat('en-US', { timeZone: zone, hour: 'numeric', hour12: false }).format(new Date(w.start_at))) % 24)
    .sort((a, b) => a - b);
  if (!hours.length) return null;
  return `${String(hours[Math.floor(hours.length / 2)]).padStart(2, '0')}:00`;
}

/**
 * @param {object} a
 * @param {string} a.today                 local date
 * @param {Array}  a.habits                [{ id, label, target, unit, history, weekCount, active }]
 * @param {Array}  a.workouts              [{ date, start_at }]
 * @param {number|null} a.workoutTarget    a weekly workout-count goal's target when there is NO gym habit
 * @param {string} [a.zone]
 * @returns {{ days: string[], items: Array<{ key, kind, label, metric_type, metric_ref, days: string[], time: string|null, target: number }> }}
 */
export function buildWeekPlan({ today, habits = [], workouts = [], workoutTarget = null, zone = 'UTC' }) {
  const days = planWeekDays(today);
  const refFrom = addDays(days[0], -7), refTo = addDays(days[0], -1);
  const allowed = days.map(weekdayIdx);
  const thisWeek = days[0] === today; // planning the rest of the current week
  const doneByIdx = (dates) => [...new Set(dates.filter(d => d >= refFrom && d <= refTo).map(weekdayIdx))];
  const toDates = (idxs) => idxs.map(i => days.find(d => weekdayIdx(d) === i)).filter(Boolean);
  const items = [];
  for (const h of habits || []) {
    if (!h || h.active === false) continue;
    const target = Number(h.target);
    // Weekly targets only: a daily habit (7/week, or a "days/week" unit at 7)
    // has nothing to spread.
    if (!(target >= 1 && target <= 6)) continue;
    const already = thisWeek ? Number(h.weekCount) || 0 : 0;
    const n = Math.max(0, target - already);
    if (!n) continue;
    const pick = chooseDays(n, allowed, doneByIdx(parseHist(h.history)));
    const isGym = h.id === 'gym';
    items.push({
      key: `habit:${h.id}`, kind: isGym ? 'workout' : 'habit', label: h.label || h.id,
      // Gym plans resolve from ANY workout that day or a Gym tick (generic
      // workout word, api/lib/commitments.js matchWorkout + ledger gym logs).
      metric_type: isGym ? 'workout' : 'habit', metric_ref: isGym ? 'Gym' : (h.label || h.id),
      days: toDates(pick), time: isGym ? typicalHour(workouts, refFrom, refTo, zone) : null, target: n,
    });
  }
  const hasGym = (habits || []).some(h => h && h.id === 'gym' && h.active !== false);
  const wt = Number(workoutTarget);
  if (!hasGym && wt >= 1 && wt <= 7) {
    const pick = chooseDays(wt, allowed, doneByIdx((workouts || []).map(w => String(w.date || '').slice(0, 10))));
    items.push({ key: 'workout', kind: 'workout', label: 'Workout', metric_type: 'workout', metric_ref: 'Workout',
      days: toDates(pick), time: typicalHour(workouts, refFrom, refTo, zone), target: wt });
  }
  return { days, items };
}

/** Commitment rows for a saved plan; skips a (metric_ref, day) already open. */
export function planToCommitments(plan, { userId, existing = [] } = {}) {
  const taken = new Set((existing || [])
    .filter(c => c && (c.status === 'open' || c.status === 'needs_confirmation') && c.due_date)
    .map(c => `${String(c.metric_ref || c.text || '').toLowerCase()}|${String(c.due_date).slice(0, 10)}`));
  const rows = [];
  for (const it of plan.items || []) {
    for (const d of it.days || []) {
      const k = `${String(it.metric_ref).toLowerCase()}|${d}`;
      if (taken.has(k)) continue;
      taken.add(k);
      rows.push({ user_id: userId, text: it.label, source: 'plan', status: 'open', due_date: d, due_time: it.time || null, metric_type: it.metric_type, metric_ref: it.metric_ref });
    }
  }
  return rows;
}
